import { useCallback, useEffect, useState } from "react"

export type Theme = "light" | "dark"

export const THEME_STORAGE_KEY = "moderniser_theme"

const THEME_COLORS: Record<Theme, string> = {
  light: "#f7f7f7",
  dark: "#0f1115",
}

export function themeFromStored(value: string | null | undefined): Theme {
  return value === "dark" ? "dark" : "light"
}

function readTheme(): Theme {
  try {
    return themeFromStored(localStorage.getItem(THEME_STORAGE_KEY))
  } catch {
    return "light"
  }
}

function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme
  document
    .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_COLORS[theme])
}

export function useTheme() {
  // theme-init.js has already restored the stored choice onto <html> before
  // first paint; reading the attribute here adopts it without a second
  // opinion. readTheme is the fallback for the page that never ran it.
  const [theme, setTheme] = useState<Theme>(() =>
    themeFromStored(document.documentElement.dataset.theme ?? readTheme()),
  )

  // Apply to the document only. The stored preference is written when the
  // person chooses, in toggleTheme — an effect that wrote on every theme
  // change recorded the initial value too, so a page that started light
  // (restore failed, storage refused…) quietly overwrote the saved dark
  // choice with "light" and the preference never survived a reload.
  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  useEffect(() => {
    const syncTheme = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY) setTheme(themeFromStored(event.newValue))
    }
    window.addEventListener("storage", syncTheme)
    return () => window.removeEventListener("storage", syncTheme)
  }, [])

  const toggleTheme = useCallback(() => {
    const next = theme === "light" ? "dark" : "light"
    setTheme(next)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      // The theme still works for this page when storage is unavailable.
    }
  }, [theme])

  return { theme, toggleTheme }
}
